import React, { useContext } from 'react';
import { Box, Button, Typography, LinearProgress, Dialog, Grid } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { useHistory } from 'react-router-dom';
import useStyles from '../../../../../assets/styles/HomeStyles';
import { AuthContext } from '../../../../../context/auth';
import { USER_ACCESS_LEVEL, PRODUCT_KEY, FEATURE_KEY } from '../../../../../utils/constants';
import { isProductEnabled, isFeatureEnabled } from '../../../../../utils/productAccess';
import AccessInfoIcon from '../../../../../assets/images/DQProImages/assess-info-icon.png';
import ProgramInfoIcon from '../../../../../assets/images/DQProImages/product-info-icon.png';
import {
    clearChatStateInsiderService,
    getActiveChatInsiderProgramService,
} from '../../../../../services/learning_programs.service';
import useCompleteImpactOnboardingStep from '../../hooks/useCompleteImpactOnboardingStep';

const ORANGE   = 'var(--brand-primary, #FF6900)';
const DARK     = 'var(--brand-text, #1a1a1a)';
const MUTED    = 'var(--brand-text-muted, #888)';
const SURFACE  = 'var(--brand-surface, #fff)';
const BORDER   = 'var(--brand-border, #ebebeb)';
const GREEN    = '#B7D334';
const BLUE     = '#5B8DD9';

/* ── Stat card ──────────────────────────────────────────────────────────────── */
const StatCard = ({ value, label, greenText, barColor = GREEN, barValue = 65, sub }) => (
    <Box sx={{
        background: SURFACE,
        borderRadius: '14px',
        p: '18px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        flex: 1,
        minWidth: 0,
        border: `1px solid ${BORDER}`,
        justifyContent: 'space-between',
    }}>
        <Box sx={{width: '100%', textAlign: 'center',}}>
            <Typography sx={{ fontSize: 'clamp(30px,3.5vw,44px)', fontWeight: 800, color: ORANGE, lineHeight: 1 }}>
                {value}
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: DARK, mt: 0.5, lineHeight: 1.3 }}>
                {label}
            </Typography>
        </Box>
        <Box sx={{ width: '100%', mt: 'auto', mb:1, }}>
            {greenText && (
                <Typography sx={{ fontSize: 13, color: MUTED, fontWeight: 400, mb: 0.75 }}>
                    ↑ {greenText}
                </Typography>
            )}
            <LinearProgress
                variant="determinate"
                value={barValue}
                sx={{
                    height: 6, borderRadius: 3,
                    backgroundColor: BORDER,
                    '& .MuiLinearProgress-bar': { backgroundColor: barColor, borderRadius: 3 },
                }}
            />
            {sub && (
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: DARK, mt: 0.75 }}>{sub}</Typography>
            )}
        </Box>
    </Box>
);

/* ── AI Readiness card ──────────────────────────────────────────────────────── */
const AIReadinessCard = () => (
    <Box sx={{
        background: SURFACE,
        borderRadius: '14px',
        p: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        minWidth: { xs: 0, md: 200 },
        maxWidth: { xs: '100%', md: 220 },
        width: { xs: '100%', md: 'auto' },
        flexShrink: 0,
        border: `1px solid ${BORDER}`,
        justifyContent: 'space-between',
    }}>
        {/* Label */}
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: MUTED, letterSpacing: 1.4, textTransform: 'uppercase', mb: 0.5 }}>
            AI Readiness
        </Typography>

        {/* Gauge — orange arc, dark score, no segment labels */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <svg width="160" height="130" viewBox="0 0 160 130">
                {/* Grey track — ~270° arc from 135° to 45° (going clockwise through bottom) */}
                {/* center (80,85), r=55 */}
                {/* 135°: x=80+55*cos(135)=80-38.9=41.1, y=85+55*sin(135)=85+38.9=123.9 */}
                {/* 45°:  x=80+55*cos(45)=80+38.9=118.9, y=85+55*sin(45)=85+38.9=123.9 */}
                <path
                    d="M 41.1 123.9 A 55 55 0 1 1 118.9 123.9"
                    fill="none" stroke={BORDER} strokeWidth="13" strokeLinecap="round"
                />
                {/* Orange fill — 71% of 270° = 191.7° → end angle = 135+191.7=326.7° */}
                {/* cos(326.7°)=0.837, sin(326.7°)=-0.547 */}
                {/* end: (80+55*0.837, 85+55*(-0.547)) = (126, 54.9) */}
                <path
                    d="M 41.1 123.9 A 55 55 0 1 1 126 54.9"
                    fill="none" stroke={ORANGE} strokeWidth="13" strokeLinecap="round"
                />
                {/* Score */}
                <text x="80" y="84" fontSize="34" fontWeight="800" fill={DARK} textAnchor="middle" fontFamily="Roboto,sans-serif" dominantBaseline="middle">71</text>
                {/* Proficient label */}
                <text x="80" y="102" fontSize="11" fontWeight="500" fill={MUTED} textAnchor="middle" fontFamily="Roboto,sans-serif">Proficient</text>
                {/* +2.5 this term */}
                <text x="80" y="118" fontSize="10" fontWeight="600" fill={ORANGE} textAnchor="middle" fontFamily="Roboto,sans-serif">+2.5 this term</text>
            </svg>
        </Box>

        {/* Bottom stats */}
        <Box sx={{ display: 'flex', gap: 0, width: '100%', justifyContent: 'space-around' }}>
            {[{ v: '+7', l: 'vs national', c: DARK }, { v: '64', l: 'National', c: DARK }, { v: '58', l: 'Global', c: DARK }].map(({ v, l, c }) => (
                <Box key={l} sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: c }}>{v}</Typography>
                    <Typography sx={{ fontSize: 10, color: MUTED }}>{l}</Typography>
                </Box>
            ))}
        </Box>
    </Box>
);

/* ── Insight column ─────────────────────────────────────────────────────────── */
const InsightItem = ({ pct, text, btnLabel, onClick }) => (
    <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        flex: 1, px: 1.5, py: { xs: 2, sm: 4 }, justifyContent: 'space-between', gap: 1, alignSelf: 'stretch',
    }}>
        <Typography sx={{ fontSize: 'clamp(26px,3vw,38px)', fontWeight: 800, color: DARK, lineHeight: 1 }}>
            {pct}
        </Typography>
        <Typography sx={{ fontSize: 14, color: DARK, textAlign: 'center', lineHeight: 1.4, flexGrow: 1 }}>
            {text}
        </Typography>
        <Button size="small" sx={{
            borderRadius: '6px', textTransform: 'none', fontWeight: 700, fontSize: 12,
            border: `1px solid ${ORANGE}`, color: ORANGE, background: SURFACE,
            px: 1.5, py: 0.3, flexShrink: 0,
            cursor: 'not-allowed',
            '&:hover': { background: SURFACE },
        }}>{btnLabel}</Button>
    </Box>
);

/* ── Gap row ────────────────────────────────────────────────────────────────── */
const GapRow = ({ text }) => (
    <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        border: `1.5px solid ${ORANGE}`, borderRadius: '12px', px: 2, py: 1.25, gap: 2,
        background: SURFACE,
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: ORANGE, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 15, color: DARK, lineHeight: 1.3 }}>{text}</Typography>
        </Box>
        <Button size="small" sx={{
            border: `1px solid ${BORDER}`, backgroundColor: BORDER, borderRadius: '10px', color: DARK, textTransform: 'none',
            fontWeight: 600, fontSize: 14, px: 3, py: 2, flexShrink: 0, minWidth: 'unset',
            '&:hover': { background: BORDER, borderColor: BORDER },
        }}>Deploy →</Button>
    </Box>
);

/* ── Main ───────────────────────────────────────────────────────────────────── */
const ImpactTab = ({ isTabView = false }) => {
    const { authUser } = useContext(AuthContext);
    const isLevel22 = authUser?.access_level === USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE;
    const hasLearningPrograms = isProductEnabled(authUser, PRODUCT_KEY.LEARNING_PROGRAM);
    const hasAssessments = isProductEnabled(authUser, PRODUCT_KEY.ASSESSMENT);
    const hasCpciAssessments = isProductEnabled(authUser, PRODUCT_KEY.CPC_INDEX_ASSESSMENT);
    const hasImpactDashboard = isProductEnabled(authUser, PRODUCT_KEY.IMPACT_DASHBOARD);
    const history = useHistory();
    const classes = useStyles();

    const [open, setOpen] = React.useState(false);
    const [dialogType, setDialogType] = React.useState(null);
    const [hasExistingProgram, setHasExistingProgram] = React.useState(false);
    const [showExistingConfirmation, setShowExistingConfirmation] = React.useState(false);
    const isStartingNewRef = React.useRef(false);

    React.useEffect(() => {
        let isMounted = true;
        getActiveChatInsiderProgramService()
            .then(res => { if (isMounted) setHasExistingProgram(!!res?.data?.program_id); })
            .catch(() => {});
        return () => { isMounted = false; };
    }, []);

    useCompleteImpactOnboardingStep();

    const handleStartNew = async () => {
        if (isStartingNewRef.current) return;
        isStartingNewRef.current = true;
        try {
            const res = await getActiveChatInsiderProgramService();
            const programId = res?.data?.program_id;
            if (programId) {
                try { await clearChatStateInsiderService(programId); } catch (e) {}
            }
        } catch (e) {}
        finally {
            setOpen(false);
            setDialogType(null);
            history.push('/dq_pro/program/creation/preview');
            isStartingNewRef.current = false;
        }
    };

    const handleCloseDialog = () => {
        setOpen(false);
        setDialogType(null);
        setShowExistingConfirmation(false);
    };

    const actionCards = [
        { title: 'Create a Program',          sub: 'Design and deliver AI Literacy learning',  btnLabel: 'Create Program',   btnBg: 'var(--brand-global-1, #FF6900)', btnColor: '#fff', onClick: () => { setDialogType('program'); setOpen(true); },    show: hasLearningPrograms },
        { title: 'Run an Assessment',          sub: "Measure learners' current AI literacy",    btnLabel: 'Start Assessment', btnBg: 'var(--brand-global-2, #7B8FC7)', btnColor: '#fff', onClick: () => { setDialogType('assessment'); setOpen(true); }, show: hasAssessments },
        { title: 'Run a CPC Index Assessment', sub: 'Measure CPC Index competencies for youth', btnLabel: 'Start CPC Index Assessment', btnBg: 'var(--brand-global-3, #0097B2)', btnColor: '#fff', onClick: () => { setDialogType('cpci_assessment'); setOpen(true); }, show: hasCpciAssessments },
        { title: 'View Your School Dashboard', sub: "Monitor your learning programs' impact",   btnLabel: 'View Impact',      btnBg: '#C91C6E', btnColor: '#fff', onClick: () => history.push('/dq_pro/insights'),               show: hasImpactDashboard && authUser?.access_level !== USER_ACCESS_LEVEL.INDIVIDUAL_ACCESS_DQ_INSIDER_PORTAL },
    ].filter(c => c.show);

    const clientActionCards = [
        { title: 'School Dashboard', sub: "Monitor your learning programs' impact", btnLabel: 'Open School Dashboard', btnBg: '#C91C6E', btnColor: '#fff', onClick: () => history.push('/dq_pro/school-dashboard/overview'), show: isFeatureEnabled(authUser, FEATURE_KEY.SCHOOL_DASHBOARD) },
    ].filter(c => c.show);

    const renderActionCards = (cards) => (
        <Box sx={{ display: 'flex', gap: 2, mt: '20px', flexShrink: 0, pb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            {cards.map(({ title, sub, btnLabel, btnBg, btnColor, onClick }) => (
                <Box key={title} sx={{
                    flex: '1 1 0', maxWidth: { sm: 'calc(33.333% - 11px)' },
                    background: SURFACE, borderRadius: '14px',
                    p: '20px 24px', display: 'flex', flexDirection: 'column',
                    gap: 1, border: `1px solid ${BORDER}`,
                }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: DARK }}>{title}</Typography>
                    <Typography sx={{ fontSize: 14, color: MUTED, flexGrow: 1 }}>{sub}</Typography>
                    <Box sx={{ mt: 1 }}>
                        <Button onClick={onClick} sx={{
                            background: btnBg, color: btnColor, textTransform: 'none',
                            fontWeight: 600, fontSize: 14, borderRadius: '8px',
                            px: 2.5, py: 0.75,
                            '&:hover': { background: btnBg, opacity: 0.9 },
                        }}>{btnLabel}</Button>
                    </Box>
                </Box>
            ))}
        </Box>
    );

    return (
    <>
    <Box sx={{
        height: '100%',
        pt: '18px',
        px: { xs: 2, sm: isTabView ? '30px' : 2, md: isTabView ? '30px' : 0 },
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
        overflowY: 'auto',
        boxSizing: 'border-box',
    }}>
        {/* Title */}
        <Typography sx={{ fontSize: { xs: 22, md: 35 }, fontWeight: 800, mb: { xs: 1, md: '38px' }, textAlign: 'center', color: DARK }}>
            Your Impact Overview
        </Typography>

        {/* Row 1: AI Readiness + 4 stat cards */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch', flexShrink: 0, flexDirection: { xs: 'column', md: 'row' } }}>
            <AIReadinessCard />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, flex: 1, minWidth: 0 }}>
                <StatCard value="2,123" label="Learners Assessed"    greenText="85% of school"    barColor={ORANGE} barValue={85} sub="187 of 220 eligible" />
                <StatCard value="65%"   label="Measurably Improved"  greenText="national avg. 58%" barColor={GREEN}  barValue={65} sub="vs national 58%" />
                <StatCard value="45%"   label="Intend to Change"     greenText="national avg. 58%" barColor={GREEN}  barValue={45} sub="vs national 58%" />
                <StatCard value="43%"   label="Learners At-Risk"     greenText="national avg. 58%" barColor={GREEN}  barValue={43} sub="vs national 58%" />
            </Box>
        </Box>

        {/* Row 2: Actionable Insights + Top 3 Biggest Gaps */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', mt: 1, flexShrink: 0, flexDirection: { xs: 'column', md: 'row' } }}>

            {/* Actionable Insights */}
            <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: DARK, mb: 1 }}>
                    Actionable Insights
                </Typography>
                <Box sx={{
                    border: `1.5px solid ${ORANGE}`,
                    borderRadius: '14px',
                    p: '12px 10px',
                    flex: 1,
                    display: 'flex',
                    background: SURFACE,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'stretch', flex: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <InsightItem pct="64%" text="of learners around the world are at risk for video gaming disorder. What about your students?" btnLabel="Run Assessment" />
                        <Box sx={{ width: { xs: '100%', sm: '1px' }, height: { xs: '1px', sm: 'auto' }, background: BORDER, alignSelf: 'stretch', mx: { xs: 0, sm: 0.5 }, my: { xs: 0.5, sm: 0 } }} />
                        <InsightItem pct="27%" text={<>of your learners age 8 from <strong>ABC class</strong> DO NOT know how to use AI safely</>} btnLabel="Create Program" />
                        <Box sx={{ width: { xs: '100%', sm: '1px' }, height: { xs: '1px', sm: 'auto' }, background: BORDER, alignSelf: 'stretch', mx: { xs: 0, sm: 0.5 }, my: { xs: 0.5, sm: 0 } }} />
                        <InsightItem pct="88%" text="of your learners have met online strangers offline" btnLabel="Assign Learning"/>
                    </Box>
                </Box>
            </Box>

            {/* Top 3 Biggest Gaps */}
            <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: DARK, mb: 1 }}>
                    Top 3 Biggest Gaps
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', flex: 1 }}>
                    {[1, 2, 3].map(i => (
                        <GapRow key={i} text={<><strong>Biggest gap:</strong> AI Footprint (DQ-12)<br/> 1 of 5 classes — score 62</>} />
                    ))}
                </Box>
            </Box>
        </Box>

        {/* Bottom action cards — hidden in tab view; owner vs client card sets */}
        {!isTabView && !isLevel22 && actionCards.length > 0 && renderActionCards(actionCards)}
        {!isTabView && isLevel22 && clientActionCards.length > 0 && renderActionCards(clientActionCards)}
    </Box>

    {/* Program / Assessment creation dialog — same flow as home page */}
    <Dialog
        open={open}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
            sx: {
                border: '2px solid #000000',
                borderRadius: '15px',
                overflow: { xs: 'scroll', sm: 'hidden' },
            }
        }}
    >
        {(dialogType === 'assessment' || dialogType === 'cpci_assessment') ? (
            <Box sx={{ p: 4, position: 'relative' }}>
                <IconButton aria-label="close" onClick={handleCloseDialog} sx={{ position: 'absolute', right: 8, top: 8, color: '#999' }}>
                    <CloseIcon />
                </IconButton>
                <Typography variant="h5" sx={{ mt: 2, mb: 4, textAlign: 'center', color: '#ff5757', fontWeight: 'bold' }}>
                    {dialogType === 'cpci_assessment'
                        ? 'Create Your Own CPC Index Assessment'
                        : 'Create Your Own AI Readiness Assessment'}
                </Typography>
                <Grid container spacing={4}>
                    <Grid item xs={12} sm={4} md={4} sx={{ mx: { sm: 'auto' } }}>
                        <Box sx={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <img src={AccessInfoIcon} alt="assessment" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </Box>
                        <Button className={classes.popupButton} variant="contained" sx={{ width: '100%', mt: 4 }}
                            onClick={() => {
                                handleCloseDialog();
                                history.push(dialogType === 'cpci_assessment'
                                    ? '/dq_pro/products/cpci/assessments'
                                    : '/dq_pro/products/assessments');
                            }}
                        >
                            Create Now
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        {dialogType === 'cpci_assessment' ? (
                            <>
                                <Typography sx={{ mb: 2 }}><strong>Child Protection &amp; Competency Index:</strong> Measure how ready and protected young people are across the CPC Index competencies.</Typography>
                                <Typography sx={{ mb: 2 }}><strong>Reports for All Stakeholders:</strong> Individual scorecards, parent reports, and school dashboards to monitor progress and risk levels.</Typography>
                                <Typography sx={{ mb: 2 }}><strong>Evidence-Based Decisions:</strong> Help your clients confidently inform curriculum development and policy-making with solid, actionable data.</Typography>
                                <Typography sx={{ mb: 2 }}><strong>Comprehensive by default:</strong> Every CPC Index indicator for the selected target audience is included automatically.</Typography>
                            </>
                        ) : (
                            <>
                                <Typography sx={{ mb: 2 }}><strong>Clear Insights:</strong> Help your learners rapidly identify their strengths and areas needing improvement in AI readiness with individual scorecards.</Typography>
                                <Typography sx={{ mb: 2 }}><strong>Reports for All Stakeholders:</strong> Parent reports and school dashboards to monitor progress and levels of AI readiness.</Typography>
                                <Typography sx={{ mb: 2 }}><strong>Evidence-Based Decisions:</strong> Enable your clients to confidently inform curriculum development and policy-making with solid, actionable data.</Typography>
                                <Typography sx={{ mb: 2 }}><strong>Curate to your target audience and needs:</strong> Select from a list of indicators that you want to assess for any demographic.</Typography>
                            </>
                        )}
                    </Grid>
                </Grid>
            </Box>
        ) : showExistingConfirmation ? (
            <Box sx={{ p: 4, position: 'relative' }}>
                <IconButton aria-label="close" onClick={handleCloseDialog} sx={{ position: 'absolute', right: 8, top: 8, color: '#999' }}>
                    <CloseIcon />
                </IconButton>
                <Typography variant="h6" sx={{ mt: 2, mb: 4, textAlign: 'center', color: '#ff5757' }}>
                    You already have an existing program in progress. <br />Do you want to continue with the existing program or start a new program?
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 4 }}>
                    <Button className={classes.popupButton} variant="contained" sx={{ width: '30%', fontSize: '15px !important' }}
                        onClick={() => { handleCloseDialog(); history.push('/dq_pro/program/creation/preview'); }}
                    >
                        Continue
                    </Button>
                    <Button className={classes.popupButton} variant="outlined"
                        sx={{ width: '30%', borderColor: '#ff5757', color: '#ff5757', fontWeight: 'bold', fontSize: '15px !important' }}
                        onClick={() => { handleCloseDialog(); handleStartNew(); }}
                    >
                        Start New Program
                    </Button>
                </Box>
            </Box>
        ) : (
            <Box sx={{ p: 4, position: 'relative' }}>
                <IconButton aria-label="close" onClick={handleCloseDialog} sx={{ position: 'absolute', right: 8, top: 8, color: '#999' }}>
                    <CloseIcon />
                </IconButton>
                <Typography variant="h5" sx={{ mt: 2, mb: 4, textAlign: 'center', color: '#ff5757', fontWeight: 'bold' }}>
                    Powerful Curriculum and AI/Digital Literacy Program Creation
                </Typography>
                <Grid container spacing={4}>
                    <Grid item xs={12} sm={4} md={4} sx={{ mx: { sm: 'auto' } }}>
                        <Box sx={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <img src={ProgramInfoIcon} alt="program" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </Box>
                        <Button className={classes.popupButton} variant="contained" sx={{ width: '100%', mt: 4 }}
                            onClick={() => {
                                if (hasExistingProgram) {
                                    setShowExistingConfirmation(true);
                                } else {
                                    handleStartNew();
                                }
                            }}
                        >
                            Build a Program
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Typography sx={{ mb: 2 }}><strong>Structured Curricula for Any Group:</strong> Generate age-appropriate learning programs organized into clear modules, units, and lessons—perfect for schools, organizations, or community groups.</Typography>
                        <Typography sx={{ mb: 2 }}><strong>Backed by 100+ International Standards and Frameworks:</strong> All content is high-quality, curated by experts, and backed by 100+ international standards and frameworks to ensure accuracy, relevance, and impact.</Typography>
                        <Typography sx={{ mb: 2 }}><strong>Media-Rich and Engaging:</strong> Every lesson comes with designed slides with images, videos, and interactive activities to make learning fun and visually engaging.</Typography>
                    </Grid>
                </Grid>
            </Box>
        )}
    </Dialog>
    </>
    );
};

export const ImpactDashboard = ImpactTab;
export { ImpactTab };
export default ImpactTab;
